import axios from "axios";
import Qs from "qs";
import { Environment } from "../shared/core/Environment";
import { RequestClass } from "./core/olga/RequestClass";
import OcdpAPI from "./OcdpAPI";

export default class OcdpAPI2 {
	client = null;
	environment_id = null;
	token = null;

	static cancel_sources = {};

	constructor() {
		this.client = new RequestClass(
			process.env.REACT_APP_SERVICES_ENDPOINT_BASE
		);
		this.environment_id = Environment.id;
		this.token = Environment.token;
	}

	async query(schema, options) {
		const { brand_id, unit_id, filters, limit, sort = "LAST_ORDER_DESC", version = 'V2' } = options;
		
		if (version == 'V1') {
			const ocdp_api = new OcdpAPI();
			return await ocdp_api.query(schema, options);
		}
		
		const response = await this.client.post({
			service: "environments",
			endpoint: `/ocdp/v2/query`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
			data: {
				environment_id: this.environment_id,
				brand_id,
				unit_id,
				schema,
				filters,
				sort,
				limit,
			},
		});

		return response.getData({ execution_id: null });
	}

	async status(execution_id, version = 'V2') {
		if (version == 'V1') {
			const ocdp_api = new OcdpAPI();
			return await ocdp_api.status(execution_id);
		}
		
		const response = await this.client.get({
			service: "environments",
			endpoint: `/ocdp/v2/status/${execution_id}`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
		});

		return response.getData({ status: "ERROR" }).status || "ERROR";
	}

	async export_status(job_id) {
		const response = await this.client.get({
			service: "environments",
			endpoint: `/ocdp/v2/export/${job_id}/status`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
		});

		return response.getData({ status: "ERROR" }).status || "ERROR";
	}

	async totals(job_id, params) {
		if (params?.version == 'V1') {
			const ocdp_api = new OcdpAPI();
			return await ocdp_api.stats(job_id, params);
		}

		const response = await this.client.get({
			service: "environments",
			endpoint: `/ocdp/v2/stats/${job_id}/total`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
		});

		return response.getData({ total: 0 }).total || 0;
	}

	async cache_totals(date) {
		const response = await this.client.get({
			service: "environments",
			endpoint: `/ocdp/v2/cache/${date}/total`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
		});

		return response.getData({ customers: 0 }).customers || 0;
	}

	async stats(execution_id, { filters = {}, version = 'V2' } = {}) {
		try {
			const query_string = Qs.stringify(
				{ filters },
				{
					addQueryPrefix: false,
					arrayFormat: "indices",
				}
			);

			const cancel_token = OcdpAPI2.register_cancel_source("stats");
			const response = await new Promise(async (resolve) => {

				const sleep = async (ms) => await new Promise((sleep_resolve) => setTimeout(sleep_resolve, ms));

				while (true) {
					const response = await this.client.get({
						service: "environments",
						endpoint: `/ocdp/v2/stats/${execution_id}?${query_string}`,
						ignoreVersion: true,
						cancelToken: cancel_token,
						headers: {
							Authorization: `Bearer ${this.token}`,
						},
					});

					const data = response.getData({ status: 'ERROR' });
					// console.log(data);
					if (!data.status) {
						return resolve(data.stats);
					}

					if (data?.status == 'READY') {
						if (data?.link) {
							const stats_response = await axios.request({
								url: data.link
							});
							// console.log(typeof stats_response.data);
							// console.log(stats_response?.data);
							return resolve(stats_response?.data?.data || {});
						}
						return resolve(data.stats || {});
					}

					if (data?.status == 'ERROR') {
						return resolve({});
					}

					await sleep(1000);
				}
			});

			OcdpAPI2.unregister_cancel_source("stats");
			return { stats: response };
		} catch (err) {
			return {};
		}
	}

	async fetch(
		execution_id,
		{ brand_id, unit_id, filters = {}, page = 0, size = 200, term = null, version = 'V2' }
	) {
		try {
			const params = {
				brand_id,
				unit_id,
				term: term,
				filters: filters || {},
				page_number: Math.max(0, page),
				page_size: size,
			};

			const query_string = Qs.stringify(params, {
				addQueryPrefix: false,
				arrayFormat: "indices",
			});

			const response = await this.client.get({
				service: "environments",
				endpoint: `/ocdp/v2/fetch/${execution_id}?${query_string}`,
				ignoreVersion: true,
				cancelToken: OcdpAPI2.register_cancel_source("fetch"),
				headers: {
					Authorization: `Bearer ${this.token}`,
				},
			});
			OcdpAPI2.unregister_cancel_source("fetch");

			return response.getData({
				term_occurrences: 0,
				page_number: page,
				page_size: size,
				page_items: [],
			});
		} catch (err) {
			return {
				term_occurrences: 0,
				page_number: page,
				page_size: size,
				page_items: [],
			};
		}
	}

	async infos(customer_id, { brand_id, unit_id }) {
		const response = await this.client.get({
			service: "environments",
			endpoint: `/ocdp/v2/data/${customer_id}/infos`,
			params: {
				brand_id,
				unit_id,
			},
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
		});

		return response.getData({});
	}

	async orders(customer_id, { brand_id, unit_id, page = 0, size = 18 }) {
		const response = await this.client.get({
			service: "environments",
			endpoint: `/ocdp/v2/data/${customer_id}/orders`,
			ignoreVersion: true,
			params: {
				brand_id,
				unit_id,
				page: Math.max(0, page),
				limit: Math.max(200, size),
			},
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
		});

		return response.getData({});
	}

	async getRegions() {
		const response = await this.client.get({
			service: "environments",
			endpoint: `/ocdp/v2/values/regions`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
		});

		return response.getData({});
	}

	async getSkus() {
		const response = await this.client.get({
			service: "environments",
			endpoint: `/ocdp/v2/values/skus`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
		});

		return response.getData({});
	}

	async getCities() {
		const response = await this.client.get({
			service: "environments",
			endpoint: `/ocdp/v2/values/cities`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
		});

		return response.getData({});
	}

	async getStates() {
		const response = await this.client.get({
			service: "environments",
			endpoint: `/ocdp/v2/values/states`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
		});

		return response.getData({});
	}

	async events(customer_id, { brand_id, unit_id, page = 0, size = 18 }) {
		const response = await this.client.get({
			service: "environments",
			endpoint: `/ocdp/v2/data/${customer_id}/events`,
			ignoreVersion: true,
			params: {
				brand_id,
				unit_id,
				page: Math.max(0, page),
				limit: Math.max(200, size),
			},
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
		});

		return response.getData({});
	}

	async audiences() {
		const response = await this.client.get({
			service: "environments",
			endpoint: `/ocdp/v2/audiences`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
		});

		return response.getData([]);
	}

	async audience(audience_id) {
		const response = await this.client.get({
			service: "environments",
			endpoint: `/ocdp/v2/audiences/${audience_id}`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
		});

		return response.getData(null);
	}

	async upsert_audience(
		audience_id,
		{
			brand_id,
			unit_id,
			name,
			type,
			filters,
			boxes,
			links,
			schema,
			sort,
			limit,
		}
	) {
		const params = {
			service: "environments",
			endpoint: `/ocdp/v2/audiences`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
			data: {
				brand_id,
				unit_id,
				name: name,
				type: type,
				filters: filters,
				schema: schema,
				boxes: boxes,
				links: links,
				sort: sort,
				limit: limit,
			},
		};

		if (audience_id) {
			params.endpoint += `/${audience_id}`;
			const response = await this.client.put(params);
			return response.getData({ audience_id: null, execution_id: null });
		} else {
			const response = await this.client.post(params);
			return response.getData({ audience_id: null, execution_id: null });
		}
	}

	async rename_audience(audience_id, name) {
		const params = {
			service: "environments",
			endpoint: `/ocdp/v2/audiences/${audience_id}/rename`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
			data: {
				name: name
			},
		};

		const response = await this.client.put(params);
		return response.getData(null);
	}

	async delete_audience(audience_id) {
		const response = await this.client.delete({
			service: "environments",
			endpoint: `/ocdp/v2/audiences/${audience_id}`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
		});

		return response.getData(null);
	}
	
	async hide_audience(audience_id) {
		const response = await this.client.delete({
			service: "environments",
			endpoint: `/ocdp/v2/audiences/${audience_id}/hide`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
		});

		return response.getData(null);
	}

	async export(execution_id, audience_id = null, filters = []) {
		const response = await this.client.post({
			service: "environments",
			endpoint: `/ocdp/v2/export`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
			data: {
				execution_id,
				audience_id,
				filters,
			},
		});

		return response.getData({ job_id: null, link: null });
	}

	async installAudiences() {
		const response = await this.client.put({
			service: "environments",
			endpoint: `/ocdp/v2/audiences/install/${this.environment_id}`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
		});

		return response.getData({ success: false });
	}

	async getMarkups() {
		const response = await this.client.get({
			service: "environments",
			endpoint: `/ocdp/v2/tags`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
		});

		return response.getData([]);
	}

	async postMarkups(data = {}) {
		const response = await this.client.post({
			service: "environments",
			endpoint: `/ocdp/v2/tags`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
			data
		});

		return response;
	}

	async deleteMarkup(tag_id) {
		const response = await this.client.delete({
			service: "environments",
			endpoint: `/ocdp/v2/tags/${tag_id}`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
		});

		return response;
	}

	async putMarkup(tag_id, data = {}) {
		const response = await this.client.put({
			service: "environments",
			endpoint: `/ocdp/v2/tags/${tag_id}`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
			data
		});

		return response.getData({ success: false });
	}

	async assignMarkupsToExecution(execution_id, tags) {
		const response = await this.client.put({
			service: "environments",
			endpoint: `/ocdp/v2/executions/${execution_id}/tags`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
			data: {
				tags
			}
		});

		return response;
	}

	async assignMarkupToCustomer(customer_id, tags = []) {
		const response = await this.client.put({
			service: "environments",
			endpoint: `/ocdp/v2/customers/${customer_id}/tags`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
			data: {
				tags
			}
		});

		return response;
	}

	async assignMarkupToOrder(order_id, tags = []) {
		const response = await this.client.put({
			service: "environments",
			endpoint: `/ocdp/v2/orders/${order_id}/tags`,
			ignoreVersion: true,
			headers: {
				Authorization: `Bearer ${this.token}`,
			},
			data: {
				tags
			}
		});

		return response;
	}

	static register_cancel_source(request_alias) {
		OcdpAPI2.cancel_sources[request_alias] = axios.CancelToken.source();
		return OcdpAPI2.cancel_sources[request_alias].token;
	}

	static unregister_cancel_source(request_alias) {
		OcdpAPI2.cancel_sources[request_alias] = null;
	}

	static is_request_running(request_alias) {
		return !!OcdpAPI2.cancel_sources[request_alias];
	}

	static cancel_request(request_alias) {
		if (OcdpAPI2.cancel_sources[request_alias]) {
			OcdpAPI2.cancel_sources[request_alias]?.cancel();
			OcdpAPI2.cancel_sources[request_alias] = null;
		}
	}
}
